/*
---
covers:
  - frontend/src/pages/sgsst/expediente/expediente.tsx
  - frontend/src/components/expediente-chrome.tsx
  - frontend/src/components/certificacion-tab.tsx
  - frontend/src/components/certificacion-explicacion.tsx
  - frontend/src/pages/sgsst/expediente/certificacion-contenido.tsx
  - frontend/src/components/sgsst/audit-export-status.tsx
  - frontend/src/pages/salud/carpeta_visita/carpetas-visita.tsx
  - frontend/src/hooks/salud/carpeta_visita/use-carpetas-visita.ts
  - backend/app/salud/carpeta_visita/routers.py
  - frontend/src/hooks/sgsst/expediente/use-certificacion.ts
  - backend/app/sgsst/expediente/routers/sgsst_certificacion.py
revisado: 2026-09-28
---
*/
import type {ManualSeccion} from '../types'

export const seccion: ManualSeccion = {
  id: 'certificacion-y-auditoria',
  titulo: 'Certificación y auditoría',
  resumen:
    'Qué es una versión certificada —la foto congelada y firmada de un documento tuyo—, quién puede firmarla con su licencia SST verificada, y los soportes que quedan listos para una auditoría externa.',
  enCorto: [
    'Fotos firmadas de sus documentos del SG-SST',
    'Certifique una versión; consúltela sin descargar nada',
    'La carpeta para la visita, en Expediente',
    'Si el registro cambia, hay que recertificar',
  ],
  replayId: 'certificacion-y-auditoria',
  operaciones: [
    {
      id: 'que-es-version-certificada',
      titulo: 'Entender qué es una versión certificada',
      como: 'En cualquier documento certificable (matriz de riesgos, matriz legal, plan de trabajo, exámenes, indicadores) vas a ver dos cosas distintas: el documento vivo, que cambia todos los días con tu operación, y su pestaña «Versión certificada». Una versión certificada es una foto de ese documento: congelada en una fecha, firmada por quien responde por ella y guardada para siempre. La pestaña te lo explica ahí mismo antes de pedirte que certifiques nada.',
      porQueImporta:
        'Es la diferencia entre tener documentos y tener cumplimiento demostrable. Tu matriz viva siempre muestra el presente, y por eso no prueba qué había en una fecha; la versión certificada sí, porque tiene fecha cierta, firma y —cuando la firma un profesional con licencia SST— esa licencia congelada tal como estaba ese día. Es la que le muestras a un inspector, y es la que Kairos guarda en tu expediente y mete en el zip de auditoría.',
    },
    {
      id: 'certificar-artefacto',
      titulo: 'Certificar un artefacto del SG-SST',
      como: 'Cada documento certificable tiene su propia pestaña «Versión certificada»; el botón «Certificar» abre un modal que te dice exactamente qué vas a congelar y, al aceptar, genera la versión vigente con fecha de firma y de vencimiento (un año, con corte al 31 de diciembre).',
      porQueImporta:
        'Lo que queda es lo que te sirve: un documento que no se borra nunca, la firma de quien lo certificó con su licencia congelada, y los estándares de la Res. 0312 que ese documento respalda marcados «Cumple» — un solo acto puede respaldar varios a la vez. Certificar exige tu licencia SST verificada (Res. 908/2025): sin ella el botón no aparece y en su lugar ves el banner que explica el porqué.',
    },
    {
      id: 'ver-lo-certificado',
      titulo: 'Ver lo que certificaste sin descargar nada',
      como: 'En la pestaña «Versión certificada» de cada documento, el enlace «Ver la matriz certificada» (o su equivalente) abre una página dedicada con el contenido de esa versión — la tabla completa, más el sello de quién firmó, cuándo y hasta cuándo rige.',
      porQueImporta:
        'Ahí ves exactamente lo que se firmó, congelado ese día: aunque después edites el documento vivo, esa página nunca se reescribe, y por eso sirve como prueba. Así revisas lo certificado sin abrir el Excel ni el Word.',
    },
    {
      id: 'usar-lo-certificado-para-cumplir',
      titulo: 'Usar una versión certificada para cumplir un estándar',
      como: 'La versión vigente lista los estándares que respalda con su estado actual. El que todavía no está en «Cumple» trae el botón «Usar para cumplir», que te lleva a su fila del checklist para que tú marques o adjuntes.',
      porQueImporta:
        'Kairos propone, tú decides: el botón nunca marca el estándar por su cuenta, porque la responsabilidad legal del SG-SST es del empleador. Los estándares fuera de tu capítulo aparecen como «Auto-acreditado (art. 27)» y no ofrecen botón — no hay nada que marcar, aunque el documento sí viaja en el zip de auditoría.',
    },
    {
      id: 'ver-deriva-y-recertificar',
      titulo: 'Ver la deriva y recertificar cuando cambia el registro',
      como: 'Si el registro subyacente cambia después de certificar (por ejemplo, incorporas una norma nueva a la matriz legal), la pestaña muestra el banner «Deriva» con el detalle de los cambios; el botón «Certificar de nuevo» genera la siguiente versión.',
      porQueImporta:
        'La versión certificada anterior nunca se borra — queda en el expediente — pero el banner de deriva te avisa, sin bloquear, que ya no refleja el estado actual, para que decidas cuándo recertificar.',
    },
  ],
}
